import { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

// Custom hook for table sorting
export const useTableSort = (initialData = [], defaultSort = null) => {
  const [sortConfig, setSortConfig] = useState(
    defaultSort || {
      key: '',
      direction: 'desc' // Default to descending for LIFO
    }
  );

  const sortData = (data, sortKey, sortDirection) => {
    // Ensure data is an array and handle undefined/null cases
    const safeData = Array.isArray(data) ? data : [];
    if (!sortKey) return safeData;

    const sortedData = [...safeData].sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];

      // Special handling for invoice/bill numbers (serial numbers) - LIFO by default
      if (sortKey === 'number' || sortKey === 'invoiceNumber' || sortKey === 'invoiceNo' || 
          sortKey === 'billNumber' || sortKey === 'challanNumber' || sortKey === 'receiptNumber' ||
          sortKey === 'docNo' || sortKey === 'paymentNo' || sortKey === 'refNo' ||
          sortKey.endsWith('No') || sortKey.endsWith('Number')) {
        const result = sortFunctions.serialNumber(aValue, bValue);
        // For LIFO, we want descending order by default
        return sortDirection === 'asc' ? result : -result;
      }

      // Special handling for dates - LIFO by default
      if (sortKey === 'date' || sortKey === 'invoiceDate' || sortKey === 'challanDate' || 
          sortKey === 'billDate' || sortKey === 'createdAt' || sortKey === 'updatedAt') {
        // Enhanced date sorting that handles Firestore timestamps, Date objects, and strings
        const getTimestamp = (val) => {
          if (!val) return 0;
          if (val?.toDate) return val.toDate().getTime(); // Firestore Timestamp
          if (val?.seconds) return val.seconds * 1000; // Firestore Timestamp (seconds)
          if (val instanceof Date) return val.getTime();
          if (typeof val === 'string') {
            const parsed = new Date(val);
            return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
          }
          return 0;
        };
        const result = getTimestamp(aValue) - getTimestamp(bValue);
        if (result !== 0) {
          return sortDirection === 'asc' ? result : -result;
        }
        // Same date: secondary sort by createdAt so same-day order is chronological (for ledgers)
        if (sortKey === 'date') {
          const getCreated = (obj) => {
            const c = obj?.createdAt;
            if (!c) return 0;
            if (c?.toDate) return c.toDate().getTime();
            if (typeof c === 'string') return new Date(c).getTime();
            return 0;
          };
          const createdResult = getCreated(a) - getCreated(b);
          return sortDirection === 'asc' ? createdResult : -createdResult;
        }
        return 0;
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        const result = aValue.localeCompare(bValue);
        return sortDirection === 'desc' ? -result : result;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        // Date comparison
        const result = aValue.getTime() - bValue.getTime();
        return sortDirection === 'desc' ? -result : result;
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        // Number comparison
        const result = aValue - bValue;
        return sortDirection === 'desc' ? -result : result;
      } else if (aValue && bValue) {
        // Try to convert to numbers if possible
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          const result = aNum - bNum;
          return sortDirection === 'desc' ? -result : result;
        }
        // Fallback to string comparison
        const result = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
        return sortDirection === 'desc' ? -result : result;
      }
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      return 0;
    });

    return sortedData; // No need to reverse since we handle direction in the sort function
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedData = (data) => {
    return sortData(data, sortConfig.key, sortConfig.direction);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️'; // Neutral icon
    }
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const getSortClassName = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return 'cursor-pointer hover:text-blue-600';
    }
    return 'cursor-pointer text-blue-600 font-semibold';
  };

  return {
    sortConfig,
    handleSort,
    getSortedData,
    getSortIcon,
    getSortClassName,
    sortedData: getSortedData(initialData)
  };
};

// Utility functions for specific data types
export const sortFunctions = {
  // String sorting
  string: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
  
  // Number sorting
  number: (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0),
  
  // Date sorting
  date: (a, b) => {
    const dateA = a ? new Date(a) : new Date(0);
    const dateB = b ? new Date(b) : new Date(0);
    return dateA.getTime() - dateB.getTime();
  },
  
  // Currency sorting (removes ₹ and commas)
  currency: (a, b) => {
    const cleanA = parseFloat(String(a).replace(/[₹,\s]/g, '')) || 0;
    const cleanB = parseFloat(String(b).replace(/[₹,\s]/g, '')) || 0;
    return cleanA - cleanB;
  },
  
  // Serial number sorting with FY awareness for formats like INV25-26/001
  serialNumber: (a, b) => {
    const parseSerialParts = (val) => {
      if (!val) return { fyStart: 0, fyEnd: 0, serial: 0 };
      const str = String(val);

      // Match FY pattern in doc no: INV25-26/001, CHA24-25/10, etc.
      const fyMatch = str.match(/(\d{2})\s*-\s*(\d{2})/);
      const fyStart = fyMatch ? (parseInt(fyMatch[1], 10) || 0) : 0;
      const fyEnd = fyMatch ? (parseInt(fyMatch[2], 10) || 0) : 0;

      // Primary serial part is expected after last slash/dash
      const slashMatch = str.match(/(?:[/-])(\d+)$/);
      if (slashMatch) {
        return {
          fyStart,
          fyEnd,
          serial: parseInt(slashMatch[1], 10) || 0
        };
      }

      // Fallback: last number group in string
      const allDigits = str.match(/\d+/g);
      if (allDigits && allDigits.length > 0) {
        return {
          fyStart,
          fyEnd,
          serial: parseInt(allDigits[allDigits.length - 1], 10) || 0
        };
      }

      return { fyStart, fyEnd, serial: 0 };
    };

    const aParts = parseSerialParts(a);
    const bParts = parseSerialParts(b);

    // FY-first ordering fixes cross-FY sorting issues in bill lists.
    if (aParts.fyStart !== bParts.fyStart) return aParts.fyStart - bParts.fyStart;
    if (aParts.fyEnd !== bParts.fyEnd) return aParts.fyEnd - bParts.fyEnd;
    return aParts.serial - bParts.serial;
  },
  
  // Created/Modified date sorting
  timestamp: (a, b) => {
    const timeA = a?.createdAt?.toDate?.() || a?.createdAt || a?.updatedAt?.toDate?.() || a?.updatedAt || new Date(0);
    const timeB = b?.createdAt?.toDate?.() || b?.createdAt || b?.updatedAt?.toDate?.() || b?.updatedAt || new Date(0);
    return new Date(timeA).getTime() - new Date(timeB).getTime();
  }
};

// Predefined sort configurations for common table types
export const tableSortConfigs = {
  // Items table
  items: {
    itemName: { type: 'string', label: 'Item Name' },
    measurement: { type: 'string', label: 'Measurement' },
    defaultRate: { type: 'currency', label: 'Default Rate' },
    type: { type: 'string', label: 'Type' },
    hsnCode: { type: 'string', label: 'HSN Code' },
    gstPercentage: { type: 'number', label: 'GST %' },
    stock: { type: 'number', label: 'Stock' },
    createdAt: { type: 'timestamp', label: 'Created Date' },
    updatedAt: { type: 'timestamp', label: 'Modified Date' }
  },
  
  // Parties table
  parties: {
    firmName: { type: 'string', label: 'Firm Name' },
    personName: { type: 'string', label: 'Person Name' },
    type: { type: 'string', label: 'Type' },
    contact: { type: 'string', label: 'Contact' },
    email: { type: 'string', label: 'Email' },
    whatsapp: { type: 'string', label: 'WhatsApp' },
    gstin: { type: 'string', label: 'GSTIN' },
    address: { type: 'string', label: 'Address' },
    createdAt: { type: 'timestamp', label: 'Created Date' },
    updatedAt: { type: 'timestamp', label: 'Modified Date' }
  },
  
  // Sales/Purchase bills table - Updated for LIFO
  bills: {
    number: { type: 'serialNumber', label: 'Bill Number', defaultDirection: 'desc' },
    date: { type: 'date', label: 'Date', defaultDirection: 'desc' },
    party: { type: 'string', label: 'Party' },
    amount: { type: 'currency', label: 'Amount' },
    paid: { type: 'currency', label: 'Paid' },
    outstanding: { type: 'currency', label: 'Outstanding' },
    createdAt: { type: 'timestamp', label: 'Created Date', defaultDirection: 'desc' },
    updatedAt: { type: 'timestamp', label: 'Modified Date', defaultDirection: 'desc' }
  },
  
  // Payments table - Updated for LIFO
  payments: {
    receiptNumber: { type: 'serialNumber', label: 'Receipt Number', defaultDirection: 'desc' },
    date: { type: 'date', label: 'Date', defaultDirection: 'desc' },
    partyName: { type: 'string', label: 'Party' },
    amount: { type: 'currency', label: 'Amount' },
    mode: { type: 'string', label: 'Mode' },
    createdAt: { type: 'timestamp', label: 'Created Date', defaultDirection: 'desc' },
    updatedAt: { type: 'timestamp', label: 'Modified Date', defaultDirection: 'desc' }
  }
};

// Sortable table header component
export const SortableHeader = ({ 
  columnKey, 
  label, 
  onSort, 
  sortConfig, 
  className = "px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600",
  as = 'th', // 'th' for table headers, 'div' for grid layouts
  icon: IconComponent = null, // Optional icon component from lucide-react
  align = 'left', // 'left', 'right', or 'center'
  headerClassName = '', // Additional className for header cell
  activeHeaderClassName = 'text-blue-600 font-semibold',
}) => {
  const isActive = sortConfig.key === columnKey;
  const activeClassName = isActive ? activeHeaderClassName : '';
  const textAlign = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const justifyClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  
  const Component = as;
  
  return (
    <Component 
      className={`${className} ${activeClassName} ${textAlign} ${onSort ? '' : 'cursor-default'} ${headerClassName}`}
      onClick={() => onSort && onSort(columnKey)}
    >
      <div className={`flex items-center gap-2 ${justifyClass}`}>
        {IconComponent && (
          <IconComponent className="w-4 h-4 flex-shrink-0" />
        )}
        <span className="whitespace-nowrap">{label}</span>
        <span className="flex-shrink-0">
          {sortConfig.key === columnKey ? (
            sortConfig.direction === 'asc' ? (
              <ArrowUp className="w-3 h-3 inline-block" />
            ) : (
              <ArrowDown className="w-3 h-3 inline-block" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 inline-block opacity-50" />
          )}
        </span>
      </div>
    </Component>
  );
}; 